import * as React from 'react';
import { Icon } from '../../display';
import { Calendar } from './Calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { InputLabel, InputMessage } from '../shared';
import {
  type DatePickerSize,
  sizeHeightClasses,
  formatDate,
  parseDate,
  handleDatePickerKeyDown,
} from './dateUtils';
import { usePickerDismiss } from './usePickerDismiss';

/**
 * Size type alias for DateRangePicker (matches DatePickerSize)
 */
export type DateRangePickerSize = DatePickerSize;

/**
 * Date range value type
 */
export interface DateRange {
  start: Date | null;
  end: Date | null;
}

/**
 * DateRangePicker component props
 */
export interface DateRangePickerProps {
  /**
   * The selected date range value
   */
  value?: DateRange;
  /**
   * Callback when the date range changes
   */
  onChange?: (range: DateRange) => void;
  /**
   * Label text displayed above the inputs
   */
  label?: string;
  /**
   * Placeholder for start date input
   */
  startPlaceholder?: string;
  /**
   * Placeholder for end date input
   */
  endPlaceholder?: string;
  /**
   * Helper text displayed below the inputs
   */
  helperText?: string;
  /**
   * Whether the input is in an error state
   */
  error?: boolean;
  /**
   * Error message displayed when error is true
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Size variant of the inputs
   * @default 'md'
   */
  size?: DateRangePickerSize;
  /**
   * Minimum selectable date
   */
  minDate?: Date;
  /**
   * Maximum selectable date
   */
  maxDate?: Date;
  /**
   * Array of dates that should be disabled
   */
  disabledDates?: Date[];
  /**
   * Date format string for display
   * @default 'MM/dd/yyyy'
   */
  format?: string;
  /**
   * Custom ID prefix for the input elements
   */
  id?: string;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Name attribute prefix for form submission
   */
  name?: string;
}

/**
 * DateRangePicker component for selecting a date range.
 *
 * Features:
 * - Two inputs for start and end dates
 * - Shared calendar dropdown for range selection
 * - Visual feedback for selected range
 * - Hover preview during selection
 * - All DatePicker features (keyboard nav, constraints, etc.)
 *
 * @example
 * // Basic usage
 * <DateRangePicker
 *   label="Policy Period"
 *   onChange={(range) => console.log(range)}
 * />
 *
 * @example
 * // Controlled with constraints
 * <DateRangePicker
 *   label="Coverage Period"
 *   value={{ start: startDate, end: endDate }}
 *   onChange={setDateRange}
 *   minDate={new Date()}
 * />
 */
const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      value = { start: null, end: null },
      onChange,
      label,
      startPlaceholder = 'Start date',
      endPlaceholder = 'End date',
      helperText,
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      size = 'md',
      minDate,
      maxDate,
      disabledDates,
      format = 'MM/dd/yyyy',
      id: providedId,
      className = '',
      name,
    },
    ref
  ) => {
    // Generate stable IDs
    const reactId = React.useId();
    const idPrefix = providedId || `daterangepicker-${reactId}`;
    const startInputId = `${idPrefix}-start`;
    const endInputId = `${idPrefix}-end`;
    const helperId = `${idPrefix}-helper`;
    const calendarId = `${idPrefix}-calendar`;

    // State
    const [isOpen, setIsOpen] = React.useState(false);
    const [activeInput, setActiveInput] = React.useState<'start' | 'end' | null>(null);
    const [startInputValue, setStartInputValue] = React.useState(() =>
      formatDate(value.start, format)
    );
    const [endInputValue, setEndInputValue] = React.useState(() => formatDate(value.end, format));
    const [hoverDate, setHoverDate] = React.useState<Date | null>(null);
    const [viewDate, setViewDate] = React.useState(() => value.start || value.end || new Date());
    const [selectionPhase, setSelectionPhase] = React.useState<'start' | 'end'>('start');

    // Refs
    const containerRef = React.useRef<HTMLDivElement>(null);
    const startInputRef = React.useRef<HTMLInputElement>(null);
    const endInputRef = React.useRef<HTMLInputElement>(null);

    // Combine container ref
    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    // Update input values when controlled value changes
    React.useEffect(() => {
      setStartInputValue(formatDate(value.start, format));
      setEndInputValue(formatDate(value.end, format));
    }, [value, format]);

    // Update view date when value changes
    React.useEffect(() => {
      if (value.start || value.end) {
        /* v8 ignore start */
        setViewDate(value.start || value.end || new Date());
        /* v8 ignore stop */
      }
    }, [value]);

    // Click outside to close & Escape to close
    const handleEscapeFocus = React.useCallback(() => {
      if (activeInput === 'start') {
        startInputRef.current?.focus();
      } else {
        endInputRef.current?.focus();
      }
    }, [activeInput]);
    usePickerDismiss(containerRef, isOpen, setIsOpen, handleEscapeFocus);

    // Handlers
    const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setStartInputValue(newValue);

      const parsedDate = parseDate(newValue, format);
      if (parsedDate) {
        onChange?.({ ...value, start: parsedDate });
        setViewDate(parsedDate);
      } else if (newValue === '') {
        onChange?.({ ...value, start: null });
      }
    };

    const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setEndInputValue(newValue);

      const parsedDate = parseDate(newValue, format);
      if (parsedDate) {
        onChange?.({ ...value, end: parsedDate });
        setViewDate(parsedDate);
      } else if (newValue === '') {
        onChange?.({ ...value, end: null });
      }
    };

    const handleDateSelect = (date: Date) => {
      if (selectionPhase === 'start') {
        // Starting a new range selection
        onChange?.({ start: date, end: null });
        setStartInputValue(formatDate(date, format));
        setEndInputValue('');
        setSelectionPhase('end');
        setActiveInput('end');
      } else {
        // Completing the range
        let start = value.start;
        let end = date;

        // Ensure start is before end
        if (start && end < start) {
          [start, end] = [end, start];
        }

        onChange?.({ start, end });
        setStartInputValue(formatDate(start, format));
        setEndInputValue(formatDate(end, format));
        setSelectionPhase('start');
        setIsOpen(false);
        endInputRef.current?.focus();
      }
    };

    const handleTodayClick = () => {
      handleDateSelect(new Date());
    };

    const handleInputFocus = (input: 'start' | 'end') => {
      setActiveInput(input);
      setIsOpen(true);
      // Reset selection phase based on which input is focused
      if (input === 'start') {
        setSelectionPhase('start');
      } else if (value.start) {
        setSelectionPhase('end');
      }
    };

    const handleInputBlur = (input: 'start' | 'end', inputValue: string) => {
      // Validate and fix input on blur
      const parsedDate = parseDate(inputValue, format);
      if (input === 'start') {
        if (parsedDate) {
          setStartInputValue(formatDate(parsedDate, format));
        } else {
          setStartInputValue(formatDate(value.start, format));
        }
      } else if (parsedDate) {
        setEndInputValue(formatDate(parsedDate, format));
      } else {
        setEndInputValue(formatDate(value.end, format));
      }
    };

    const handleCalendarButtonClick = () => {
      /* v8 ignore start */
      if (!disabled) {
        /* v8 ignore stop */
        if (isOpen) {
          setIsOpen(false);
        } else {
          setIsOpen(true);
          setActiveInput('start');
          setSelectionPhase('start');
        }
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      handleDatePickerKeyDown(e, isOpen, setIsOpen);
    };

    // Determine which message to display
    const showError = error && errorMessage;
    const messageId = (showError || helperText) ? helperId : undefined;

    // Input classes
    const getInputClasses = () =>
      [
        sizeHeightClasses[size],
        'flex-1',
        'min-w-0',
        'rounded-[6px]',
        'border',
        'px-3',
        'text-[15px]',
        'leading-[22px]',
        'outline-none',
        'transition-all',
        'duration-150',
        'ease-out',
        'placeholder:text-secondary-400',
        !error && !disabled && 'border-secondary-300',
        !error && !disabled && 'focus:border-primary-400',
        !error && !disabled && 'focus:ring-2',
        !error && !disabled && 'focus:ring-primary-200',
        error && !disabled && 'border-error-700',
        error && !disabled && 'focus:ring-2',
        error && !disabled && 'focus:ring-error-700/20',
        disabled && 'bg-secondary-100',
        disabled && 'text-secondary-400',
        disabled && 'border-secondary-300',
        disabled && 'cursor-not-allowed',
      ]
        .filter(Boolean)
        .join(' ');

    return (
      <div ref={containerRef} className={`relative w-full ${className}`}>
        {/* Label */}
        {label && <InputLabel label={label} htmlFor={startInputId} required={required} />}

        {/* Inputs container */}
        <div className="relative flex items-center gap-2">
          {/* Start date input */}
          <input
            ref={startInputRef}
            id={startInputId}
            type="text"
            name={name ? `${name}-start` : undefined}
            value={startInputValue}
            onChange={handleStartInputChange}
            onFocus={() => handleInputFocus('start')}
            onBlur={() => handleInputBlur('start', startInputValue)}
            onKeyDown={handleInputKeyDown}
            placeholder={startPlaceholder}
            disabled={disabled}
            aria-invalid={error}
            aria-required={required}
            aria-describedby={messageId}
            aria-label="Start date"
            className={getInputClasses()}
            autoComplete="off"
          />

          {/* Separator */}
          <span className="text-secondary-500 shrink-0">to</span>

          {/* End date input */}
          <input
            ref={endInputRef}
            id={endInputId}
            type="text"
            name={name ? `${name}-end` : undefined}
            value={endInputValue}
            onChange={handleEndInputChange}
            onFocus={() => handleInputFocus('end')}
            onBlur={() => handleInputBlur('end', endInputValue)}
            onKeyDown={handleInputKeyDown}
            placeholder={endPlaceholder}
            disabled={disabled}
            aria-invalid={error}
            aria-describedby={messageId}
            aria-label="End date"
            className={getInputClasses()}
            autoComplete="off"
          />

          {/* Calendar icon button */}
          <button
            type="button"
            onClick={handleCalendarButtonClick}
            disabled={disabled}
            className={[
              'shrink-0',
              'p-2',
              'rounded-md',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              disabled ? 'text-secondary-400' : 'text-secondary-500',
              !disabled && 'hover:text-secondary-600',
              !disabled && 'hover:bg-secondary-100',
              'transition-colors',
              'focus:outline-none',
              'focus:ring-2',
              'focus:ring-primary-200',
            ].join(' ')}
            aria-label="Open calendar"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-controls={isOpen ? calendarId : undefined}
          >
            <Icon icon={CalendarIcon} size="sm" />
          </button>
        </div>

        {/* Calendar dropdown */}
        {isOpen && (
          <dialog
            id={calendarId}
            className="absolute z-50 mt-1 border-0 p-0 bg-transparent"
            open
            aria-modal="true"
            aria-label="Choose date range"
          >
            <Calendar
              rangeStart={value.start}
              rangeEnd={value.end}
              hoverDate={hoverDate}
              onDateSelect={handleDateSelect}
              onDateHover={setHoverDate}
              minDate={minDate}
              maxDate={maxDate}
              disabledDates={disabledDates}
              viewDate={viewDate}
              onViewDateChange={setViewDate}
              onTodayClick={handleTodayClick}
              isRangeMode
            />
          </dialog>
        )}

        {/* Helper text or error message */}
        <InputMessage error={error} errorMessage={errorMessage} helperText={helperText} helperId={helperId} />
      </div>
    );
  }
);

DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
